import {createSlice} from '@reduxjs/toolkit';
import {REHYDRATE} from 'redux-persist';
import {RootState} from './Store';

// Dedicated state for the /raas2 page's planner/orchestrator chat.
// Kept separate from `chatSliceGenie` (used by /home and /chatGenie) so the
// two chat experiences don't share messages/session/conversation state.
const initialState = {
  messageListSupervisor: [] as any[],
  convoIdSupervisor: null,
  questionText: '',
};

// A message left mid-flight (isLoading: true) when the tab closes or
// refreshes gets persisted as-is by redux-persist, so on reload it would
// stay stuck "in progress" forever (stop button shown with nothing running).
// Resolve any such message to an interrupted state as soon as it rehydrates.
const resolveStuckLoadingMessages = (messages: any[] | undefined): any[] =>
  Array.isArray(messages)
    ? messages.map((msg) =>
        msg?.isLoading
          ? {
              ...msg,
              isLoading: false,
              text: 'Request interrupted. Please try again.',
              content: 'Request interrupted. Please try again.',
              isError: true,
            }
          : msg,
      )
    : messages || [];

export const SupervisorChatSlice = createSlice({
  name: 'supervisorChat',
  initialState,
  reducers: {
    setmessageListSupervisor: (state, action) => {
      state.messageListSupervisor = action.payload;
    },

    updateMessageListSupervisor(state, action) {
      const {requestId, finalBotMessage} = action.payload;
      const messageIndex = state.messageListSupervisor.findIndex(
        (msg) => msg?.request_id === requestId,
      );
      if (messageIndex !== -1) {
        state.messageListSupervisor[messageIndex] = {
          ...state.messageListSupervisor[messageIndex],
          ...finalBotMessage,
          isLoading: false,
        };
      } else {
        state.messageListSupervisor.push({...finalBotMessage, isLoading: false});
      }
    },

    // Merges partial fields (e.g. a live progress message) into an
    // in-flight message WITHOUT forcing isLoading:false, unlike
    // updateMessageListSupervisor above (used only for the final answer).
    patchMessageListSupervisor(state, action) {
      const {requestId, patch} = action.payload;
      const messageIndex = state.messageListSupervisor.findIndex(
        (msg) => msg?.request_id === requestId,
      );
      if (messageIndex !== -1) {
        state.messageListSupervisor[messageIndex] = {
          ...state.messageListSupervisor[messageIndex],
          ...patch,
        };
      }
    },

    setConvoIdSupervisor: (state, action) => {
      state.convoIdSupervisor = action.payload;
    },
    setQuestionSupervisor: (state, action) => {
      state.questionText = action.payload;
    },
    clearChatSliceSupervisor: (state) => {
      state.messageListSupervisor = [] as any;
      state.convoIdSupervisor = null;
      state.questionText = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.messageListSupervisor = resolveStuckLoadingMessages(
        state.messageListSupervisor,
      );
    });
  },
});

export const {
  setmessageListSupervisor,
  clearChatSliceSupervisor,
  setConvoIdSupervisor,
  updateMessageListSupervisor,
  patchMessageListSupervisor,
  setQuestionSupervisor,
} = SupervisorChatSlice.actions;

export const getmessageListSupervisor = (state: RootState) =>
  state.supervisorChatSlice.messageListSupervisor;

export const getConvoIdSupervisor = (state: RootState) =>
  state.supervisorChatSlice.convoIdSupervisor;

export const getQuestionSupervisor = (state: RootState) =>
  state.supervisorChatSlice.questionText;

export default SupervisorChatSlice.reducer;
